import React, { Fragment, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { css } from '@emotion/core';
import throttle from 'lodash/throttle';
import PlayButton from '@embed/components/common/PlayButton';
import { onNavigateToVideoHelper } from '@embed/components/VideoPlayer/onNavigateToVideoHelper';
import { EventType } from '@embed/customEmbedEvent';
import { useTranslation } from '@embed/hooks/react-i18nextLazy';
import { useEmbedFeed } from '@embed/hooks/useEmbedFeed';
import useSessionStorage from '@embed/hooks/useSessionStorage';
import LogoAsset from './assets/LogoSvg';
import { BREAKPOINT, EXPAND_DISTANCE, horizontalMargin, LOGO_HEIGHT, LOGO_HEIGHT_DESKTOP, verticalMargin, } from './constants';
import Expandables from './Expandables';
import { trackFloatingChannelButton } from './tracking';
const VideoPlayer = React.lazy(() => import('@embed/components/VideoPlayer'));
export const FloatingButton = (props) => {
    var _a;
    const { appContext, params, actions, icon, positionHorizontal, positionVertical, offsetHorizontal, offsetVertical, } = props;
    const { t } = useTranslation();
    const isMobile = (window === null || window === void 0 ? void 0 : window.matchMedia) && (window === null || window === void 0 ? void 0 : window.matchMedia(BREAKPOINT).matches);
    const [closeButtonPressCount, setCloseButtonPressCount] = useSessionStorage('_fwnfltngbtnclsdprsd', 0);
    const containerRef = useRef(null);
    const [isExpanded, setIsExpanded] = useState(!params.collapsed && closeButtonPressCount < 2);
    const [isThumbnailImpressionTracked, setThumbnailImpressionTracked] = useState(false);
    const [{ videos }, feedActions] = useEmbedFeed({
        config: appContext.embedFeedConfig,
        params,
        performance: appContext.performance,
    });
    const thumbnails = useMemo(() => {
        return videos.slice(0, 3);
    }, [videos]);
    useLayoutEffect(() => {
        actions.trackEmbedImpression({
            appContext,
            container: containerRef.current,
            eventType: EventType.FW_FLOATINGBUTTON_IMPRESSION,
        });
        trackFloatingChannelButton({
            type: 'feed:create_fcb_impression',
            params,
            containerRef,
            appContext,
        });
    }, []);
    useEffect(() => {
        if (isExpanded && !isThumbnailImpressionTracked && thumbnails.length > 0) {
            setThumbnailImpressionTracked(true);
            actions.trackThumbnailImpression({
                videos: thumbnails,
                appContext,
            });
        }
    }, [
        isExpanded,
        isThumbnailImpressionTracked,
        thumbnails,
        appContext,
        actions,
    ]);
    const [videoInPlayer, setVideoInPlayer] = useState(null);
    useEffect(() => {
        if (isExpanded) {
            const handleScroll = throttle(() => {
                setIsExpanded(window.scrollY < EXPAND_DISTANCE);
            }, 250, {
                leading: false,
                trailing: true,
            });
            window.addEventListener('scroll', handleScroll, { passive: true });
            return () => {
                window.removeEventListener('scroll', handleScroll);
            };
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        const windowListener = (event) => {
            const path = event.path || (event.composedPath && event.composedPath());
            if (!videoInPlayer &&
                isExpanded &&
                !path.includes(containerRef.current)) {
                setIsExpanded(false);
            }
        };
        window.addEventListener('click', windowListener);
        return () => {
            window.removeEventListener('click', windowListener);
        };
    }, [videoInPlayer, isExpanded, containerRef.current]);
    const handleSelectVideo = useCallback((video) => {
        if (onNavigateToVideoHelper({ params, appContext, video })) {
            setVideoInPlayer(video);
        }
    }, [appContext, params, setVideoInPlayer]);
    const onAvatarMouseEnter = useCallback(() => {
        setIsExpanded(true);
        trackFloatingChannelButton({
            type: 'engagement:hover_fcb',
            params,
            appContext,
        });
    }, [params]);
    const onContainerMouseLeave = useCallback(() => {
        if (params.collapse || window.scrollY > EXPAND_DISTANCE) {
            setIsExpanded(false);
        }
    }, [params]);
    const onAvatarClick = useCallback((event) => {
        event.stopPropagation();
        if (isMobile) {
            event.preventDefault();
            setIsExpanded(true);
        }
        handleSelectVideo(videos[0]);
        trackFloatingChannelButton({
            type: 'engagement:click_fcb',
            params,
            appContext,
        });
    }, [isMobile, params, videos, handleSelectVideo]);
    const onItemMouseEnter = useCallback(({ video, index }) => (event) => {
        event.stopPropagation();
        trackFloatingChannelButton({
            type: 'engagement:hover_fcb',
            video,
            index,
            params,
            appContext,
        });
    }, [params]);
    const onItemClick = useCallback(({ video, index }) => (event) => {
        event.stopPropagation();
        handleSelectVideo(video);
        actions.trackVideoPick({ video });
        trackFloatingChannelButton({
            type: 'engagement:click_fcb',
            video,
            index,
            params,
            appContext,
        });
    }, [params, handleSelectVideo]);
    const onToggleExpand = useCallback((event) => {
        event.stopPropagation();
        setIsExpanded(!isExpanded);
        isExpanded && setCloseButtonPressCount(closeButtonPressCount + 1);
        trackFloatingChannelButton({
            type: 'engagement:close_fcb',
            params,
            appContext,
        });
    }, [closeButtonPressCount, setCloseButtonPressCount, isExpanded, params]);
    const expandables = (<Expandables videos={thumbnails} isExpanded={isExpanded} onClick={onItemClick} onMouseEnter={onItemMouseEnter} onToggleExpand={onToggleExpand} reverse={positionVertical !== 'top'}/>);
    return (<>
      <div className="cy-fcb" dir={params.dir} ref={containerRef} onMouseLeave={onContainerMouseLeave} css={css `
          font-size: 14px;
          font-family: Avenir Next, -apple-system, BlinkMacSystemFont, Segoe UI,
            Roboto, Helvetica Neue, Arial, sans-serif;
          font-weight: 300;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;

          line-height: 1.333;
          max-width: ${2 * LOGO_HEIGHT_DESKTOP}px;
          margin: 0 ${LOGO_HEIGHT_DESKTOP / 3}px;
          position: fixed;
          z-index: 2147483646;

          @media ${BREAKPOINT} {
            max-width: ${2 * LOGO_HEIGHT}px;
            margin: 0 ${LOGO_HEIGHT / 3}px;
          }

          ${positionHorizontal === 'left'
            ? `
          left: ${offsetHorizontal || horizontalMargin}px;
        `
            : `right: ${offsetHorizontal || horizontalMargin}px;`}

          ${positionVertical === 'top'
            ? `top: ${offsetVertical || verticalMargin}px;`
            : `bottom: ${offsetVertical || verticalMargin}px;`}
        `}>
        {positionVertical !== 'top' && expandables}

        <div css={css `
            cursor: pointer;
            background: linear-gradient(
              97.33deg,
              #fe3952 -57.16%,
              #d513b1 149.26%
            );
            border-radius: 50%;
            overflow: hidden;
            padding: 4px;
            height: ${LOGO_HEIGHT_DESKTOP}px;
            width: ${LOGO_HEIGHT_DESKTOP}px;
            ${positionVertical === 'top'
            ? css `
                  margin-bottom: 12px;
                `
            : css `
                  margin-top: 12px;
                `}
            position: relative;
          `} className="cy-fcb-channel" onClick={onAvatarClick} onMouseEnter={onAvatarMouseEnter}>
          {icon ? (<img css={css `
                height: 100%;
                width: 100%;
                object-fit: cover;
                border-radius: 50%;
                border: 2px solid white;
                box-sizing: border-box;
              `} src={icon}/>) : (<LogoAsset css={css `
                width: 100%;
                height: 100%;
                border-radius: 50%;
                border: 2px solid white;
                box-sizing: border-box;
              `}/>)}
          <PlayButton aria-label={t(`Play {{caption}}`, { caption: (_a = videos[0]) === null || _a === void 0 ? void 0 : _a.caption })} onClick={onItemClick({ video: videos[0] })} style={{ width: 58, height: 58 }}/>
        </div>

        {positionVertical === 'top' && expandables}
        {videoInPlayer && (<Suspense fallback={<Fragment />}>
            <VideoPlayer appContext={appContext} params={params} videos={videos} video={videoInPlayer} triggerFetchNextVideos={feedActions.loadMore} onClose={() => setVideoInPlayer(null)} actions={actions}/>
          </Suspense>)}
      </div>
    </>);
};
